import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PaymentDialog
 * Buyer-side confirmation before creating a match. The platform is 100%
 * subscription-based (paid by sellers only), so buyers never pay anything
 * here — the dialog is a pure "confirm your acceptance" step that unlocks
 * the supplier's contact details.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open) => void
 *  - previewType: "bid" | "surplus"
 *  - previewId: id of bid or surplus
 *  - onPay: async () => void — called after user confirms
 */
const PaymentDialog = ({ open, onOpenChange, previewType, previewId, previewQuantity, previewDeliveryMode, onPay }) => {
  const { getAuthHeader } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open || !previewId) {
      setPreview(null);
      return;
    }
    setLoading(true);
    axios.post(
      `${API}/matches/preview-fee`,
      { type: previewType, id: previewId, quantity: previewQuantity || null, delivery_mode: previewDeliveryMode || null },
      { headers: { Authorization: getAuthHeader() } }
    )
      .then((res) => setPreview(res.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Error al cargar el resumen'))
      .finally(() => setLoading(false));
  }, [open, previewType, previewId, previewQuantity, previewDeliveryMode]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onPay();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirmar aceptación</DialogTitle>
          <DialogDescription>
            Al confirmar se revelarán los datos de contacto del proveedor.
          </DialogDescription>
        </DialogHeader>

        {loading || !preview ? (
          <div className="py-8 text-center text-slate-500">Cargando resumen...</div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Product summary */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Vas a aceptar</p>
              <p className="text-base font-bold text-slate-900">
                {preview.quantity} {preview.format} de {preview.product}
              </p>
              <p className="text-sm text-slate-600 mt-1" data-testid="payment-preview-price">
                Precio del proveedor: €{preview.price.toFixed(2)} / {preview.format}
              </p>
              {preview.delivery_mode && (
                <p className="text-sm text-slate-700 mt-1" data-testid="payment-preview-delivery">
                  🚚 {preview.delivery_mode === 'delivery' ? 'Entrega a domicilio' : 'Recogida en el proveedor'}
                  {preview.delivery_mode === 'delivery' && preview.delivery_fee > 0 && ` · transporte €${Number(preview.delivery_fee).toFixed(2)}`}
                </p>
              )}
              {(preview.quantity > 1 || preview.delivery_fee > 0) && (
                <p className="text-sm font-semibold text-slate-900 mt-1" data-testid="payment-preview-total">
                  Total: €{(preview.total ?? preview.price * preview.quantity).toFixed(2)}
                </p>
              )}
            </div>

            {/* Free-for-buyer notice */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-2" data-testid="free-for-buyer-notice">
              <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800">
                <span className="font-semibold">Sin coste para ti.</span> La app es 100% gratis para compradores.
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={confirming}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || confirming || !preview}
            data-testid="confirm-payment-button"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {confirming ? 'Procesando...' : 'Confirmar aceptación'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
